import { useCallback } from 'react';
import { SprintAPI } from '../../utils/api';
import { 
  removeTaskLinkFromParent, 
  dispatchCampaignNestedEvent, 
  dispatchTasksUpdatedEvent 
} from './utils';

/**
 * Hook for unnesting a campaign (promoting to root level).
 * 
 * When a campaign is unnested:
 * - Its parentSprintId is cleared
 * - The task link in the old parent is removed
 * - Events are dispatched to sync SprintDetail state
 */
export const useUnnestCampaign = (setSprints) => {
  /**
   * Unnest a campaign by promoting it to root level.
   * 
   * @param {string} draggedSprintId - ID of the campaign to unnest
   * @returns {Promise<boolean>} - True if unnesting occurred, false if already at root
   */
  const unnestCampaign = useCallback(async (draggedSprintId) => {
    const draggedSprint = await SprintAPI.get(draggedSprintId);
    
    if (!draggedSprint) {
      console.error('[UNNEST] Failed to fetch dragged sprint');
      return false;
    }
    
    // If already at root level, nothing to do
    if (!draggedSprint.parentSprintId) {
      console.log('[UNNEST] Campaign is already at root level');
      return false;
    }
    
    const oldParentId = draggedSprint.parentSprintId;
    
    // Remove the task link from the old parent
    const updatedOldParentSprint = await removeTaskLinkFromParent(oldParentId, draggedSprintId);
    
    // Clear the parentSprintId on the dragged sprint
    const updatedDraggedSprint = {
      ...draggedSprint,
      parentSprintId: null,
    };
    
    // Save to API
    const apiUpdates = [SprintAPI.update(draggedSprintId, updatedDraggedSprint)];
    if (updatedOldParentSprint) {
      apiUpdates.push(SprintAPI.update(oldParentId, updatedOldParentSprint));
    }
    await Promise.all(apiUpdates);
    
    // Update local state
    setSprints(prev => prev.map(s => {
      if (s.id === draggedSprintId) return updatedDraggedSprint;
      if (updatedOldParentSprint && s.id === oldParentId) return updatedOldParentSprint;
      return s;
    }));
    
    // Dispatch events for state sync
    dispatchCampaignNestedEvent(draggedSprintId, null, null);
    
    if (updatedOldParentSprint) {
      dispatchTasksUpdatedEvent(
        oldParentId,
        updatedOldParentSprint.tasks,
        updatedOldParentSprint.nodePositions
      );
    }
    
    console.log(`[UNNEST] Promoted "${draggedSprint.name}" to root level`);
    return true;
  }, [setSprints]);

  return { unnestCampaign };
};

export default useUnnestCampaign;
